import type { ComponentPropsWithoutRef, ElementType } from 'react';

import { tv } from '../../lib/tv';
import { gapClass, type Space } from './space';

export type StackProps<T extends ElementType = 'div'> = {
  /** Element to render. Defaults to `div`; use `ul`, `section`, `form`, … for semantics. */
  as?: T;
  /** Vertical space between children, as a Figma `space/*` token. */
  gap?: Space;
  /** Horizontal alignment of children. `stretch` makes them full width. */
  align?: 'left' | 'center' | 'right' | 'stretch';
  /** Vertical distribution when the stack is taller than its content. */
  justify?: 'start' | 'center' | 'end' | 'between';
} & Omit<ComponentPropsWithoutRef<T>, 'as' | 'gap' | 'align' | 'justify'>;

const stack = tv({
  base: 'flex flex-col',
  variants: {
    gap: gapClass,
    align: {
      left: 'items-start',
      center: 'items-center',
      right: 'items-end',
      stretch: 'items-stretch',
    },
    justify: {
      start: 'justify-start',
      center: 'justify-center',
      end: 'justify-end',
      between: 'justify-between',
    },
  },
  defaultVariants: { gap: 4, align: 'stretch', justify: 'start' },
});

/**
 * Vertical layout: children in a column with an on-system gap. Equivalent to a
 * vertical auto-layout frame in Figma.
 */
export function Stack<T extends ElementType = 'div'>({
  as,
  gap,
  align,
  justify,
  className,
  ...props
}: StackProps<T>) {
  const Component: ElementType = as ?? 'div';
  return <Component className={stack({ gap, align, justify, className })} {...props} />;
}
